/**
 * Client-side hint from a GAM CSV export's metadata block — the "Label,Value"
 * records above the header row ("Report name", "Date range", "Filters", …).
 * The upload modal shows it per file before uploading; the server re-parses
 * every file and its month detection is authoritative.
 */

export interface GamCsvHint {
  reportName: string | null;
  reportResultId: string | null;
  dateRangeText: string | null;
  filtersText: string | null;
  /** "YYYY-MM" the Date range starts in (null when it can't be read) */
  month: string | null;
  /** The Date range doesn't cover exactly one full month */
  partial: boolean;
}

/** GAM's preamble is a few hundred bytes; long Filters lists stay well under this. */
const HEAD_BYTES = 16 * 1024;

const LABELS: Record<string, keyof Omit<GamCsvHint, 'month' | 'partial'>> = {
  'report name': 'reportName',
  'report result id': 'reportResultId',
  'date range': 'dateRangeText',
  filters: 'filtersText',
};

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** One CSV record's cells (quotes and "" escapes; GAM's preamble has no multi-line values). */
function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      cells.push(cell);
      cell = '';
    } else {
      cell += ch;
    }
  }
  cells.push(cell);
  return cells;
}

/** "Sep 1, 2026 - Sep 30, 2026" → its month, and whether it is exactly that whole month. */
export function monthFromDateRange(text: string | null): { month: string | null; partial: boolean } {
  if (!text) return { month: null, partial: false };
  const parts = text.split(/\s+[-–]\s+/);
  if (parts.length !== 2) return { month: null, partial: false };
  const start = new Date(parts[0].trim());
  const end = new Date(parts[1].trim());
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return { month: null, partial: false };
  const month = `${start.getFullYear()}-${pad2(start.getMonth() + 1)}`;
  const lastDay = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  return { month, partial: !sameMonth || start.getDate() !== 1 || end.getDate() !== lastDay };
}

/** Read the hint from the start of a GAM export's text. */
export function parseGamCsvHead(text: string): GamCsvHint {
  const hint: GamCsvHint = {
    reportName: null,
    reportResultId: null,
    dateRangeText: null,
    filtersText: null,
    month: null,
    partial: false,
  };
  const lines = text.replace(/^﻿/, '').split(/\r?\n/);
  for (const line of lines) {
    if (!line.trim()) break; // the blank line before the header row ends the preamble
    const [rawLabel, ...rest] = splitCsvLine(line);
    const field = LABELS[rawLabel.trim().replace(/[,:]+$/, '').trim().toLowerCase()];
    if (!field || hint[field] !== null) continue;
    const value = rest.map((c) => c.trim()).filter(Boolean).join(', ');
    hint[field] = value || null;
  }
  return { ...hint, ...monthFromDateRange(hint.dateRangeText) };
}

/** The first bytes of a file as text (FileReader: works in every browser and in jsdom). */
function readHead(file: Blob, bytes: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsText(file.slice(0, bytes));
  });
}

/** Best-effort hint for a picked file (an unreadable file just gets an empty hint). */
export async function readGamCsvHint(file: Blob): Promise<GamCsvHint> {
  try {
    return parseGamCsvHead(await readHead(file, HEAD_BYTES));
  } catch {
    return parseGamCsvHead('');
  }
}
